/**
 * Modern HTTP utility class using native Fetch API
 * Replaces axios-based Http utility with zero external dependencies
 */
export class HttpCancelError extends Error {
    constructor() {
        super();
        this.message = "Cancel Error";
        this.name = "HttpCancelError";
    }
}
// interface RequestConfig {
//   headers?: Record<string, string>;
//   cancelSignal?: AbortSignal;
// }
export default class Http {
    constructor(baseUrl = window.location.origin) {
        this.baseURL = baseUrl;
        this.abortControllers = new Map();
        // Get language from global NITECO object or HTML lang attribute
        const language = window.NITECO?.language ||
            document.querySelector("html")?.getAttribute("lang") ||
            document.querySelector("html")?.getAttribute("data-lang") ||
            "en";
        this.defaultHeaders = {
            "Content-Type": "application/json",
            language: language,
            timezone: new Date().getTimezoneOffset().toString(),
        };
    }
    /**
     * Build full URL from relative path
     */
    buildUrl(url) {
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        const cleanBase = this.baseURL.replace(/\/$/, "");
        const cleanUrl = url.replace(/^\//, "");
        return `${cleanBase}/${cleanUrl}`;
    }
    /**
     * Handle successful response
     */
    async handleResponse(response) {
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({
                message: response.statusText,
            }));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        // Try to parse as JSON, fallback to text
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            return response.json();
        }
        return response.text();
    }
    /**
     * Handle request error
     */
    handleError(error) {
        if (error.name === "AbortError") {
            return Promise.reject(new HttpCancelError());
        }
        return Promise.reject(error);
    }
    /**
     * Cancel ongoing request
     */
    cancelRequest(url) {
        const controller = this.abortControllers.get(url);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(url);
        }
    }
    /**
     * Get or create abort controller for request
     */
    getAbortSignal(url, cancelPrevious) {
        if (cancelPrevious) {
            this.cancelRequest(url);
        }
        const controller = new AbortController();
        this.abortControllers.set(url, controller);
        return controller.signal;
    }
    /**
     * Cleanup abort controller after request
     */
    cleanupController(url) {
        this.abortControllers.delete(url);
    }
    /**
     * GET request
     */
    async get(url, cancelPrevious = false, additionalHeaders = {}) {
        const signal = this.getAbortSignal(url, cancelPrevious);
        try {
            const response = await fetch(this.buildUrl(url), {
                method: "GET",
                headers: {
                    ...this.defaultHeaders,
                    ...additionalHeaders,
                },
                signal,
            });
            return await this.handleResponse(response);
        }
        catch (error) {
            return this.handleError(error);
        }
        finally {
            this.cleanupController(url);
        }
    }
    /**
     * POST request
     */
    async post(url, body, cancelPrevious = false, additionalHeaders = {}) {
        const signal = this.getAbortSignal(url, cancelPrevious);
        try {
            const response = await fetch(this.buildUrl(url), {
                method: "POST",
                headers: {
                    ...this.defaultHeaders,
                    ...additionalHeaders,
                },
                body: body ? JSON.stringify(body) : undefined,
                signal,
            });
            return await this.handleResponse(response);
        }
        catch (error) {
            return this.handleError(error);
        }
        finally {
            this.cleanupController(url);
        }
    }
    /**
     * PUT request
     */
    async put(url, body, cancelPrevious = false, additionalHeaders = {}) {
        const signal = this.getAbortSignal(url, cancelPrevious);
        try {
            const response = await fetch(this.buildUrl(url), {
                method: "PUT",
                headers: {
                    ...this.defaultHeaders,
                    ...additionalHeaders,
                },
                body: body ? JSON.stringify(body) : undefined,
                signal,
            });
            return await this.handleResponse(response);
        }
        catch (error) {
            return this.handleError(error);
        }
        finally {
            this.cleanupController(url);
        }
    }
    /**
     * DELETE request
     */
    async delete(url, cancelPrevious = false, additionalHeaders = {}) {
        const signal = this.getAbortSignal(url, cancelPrevious);
        try {
            const response = await fetch(this.buildUrl(url), {
                method: "DELETE",
                headers: {
                    ...this.defaultHeaders,
                    ...additionalHeaders,
                },
                signal,
            });
            return await this.handleResponse(response);
        }
        catch (error) {
            return this.handleError(error);
        }
        finally {
            this.cleanupController(url);
        }
    }
    /**
     * Cancel all ongoing requests
     */
    cancelAll() {
        this.abortControllers.forEach((controller) => controller.abort());
        this.abortControllers.clear();
    }
}
